import React, { useEffect, useMemo, useState } from "react";
import { Table, Tag, Select, Avatar } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";

import { getLeads, updateLeadStatus } from "../features/customers/customerSlice";
import { getAUser } from "../features/auth/authSlice";
import "../styles/leads.css"

const TeamSinglePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const leadState = useSelector((state) => state.customer.leads || []);
  const member = useSelector((state) => state.auth.singleUser);
  const isLoading = useSelector((state) => state.auth.isLoading);

  const [weekFilter, setWeekFilter] = useState("all");

  useEffect(() => {
    if (id) {
      dispatch(getLeads());
      dispatch(getAUser(id));
    }
  }, [dispatch, id]);

  const getWeekNumber = (date) => {
    const firstDay = new Date(date.getFullYear(), 0, 1);
    const pastDays = (date - firstDay) / 86400000;
    return Math.ceil((pastDays + firstDay.getDay() + 1) / 7);
  };

  const currentWeek = getWeekNumber(new Date());

  const displayName = useMemo(() => {
    if (!member) return "Team Member";

    return (
      member.agentName ||
      `${member.firstname || ""} ${member.lastname || ""}`.trim() ||
      member.name ||
      "Team Member"
    );
  }, [member]);

  const memberAgentName = useMemo(() => {
    if (!member) return "";
    return member.agentName || member.firstname || member.name || "";
  }, [member]);

  const memberLeads = useMemo(() => {
    return leadState.filter((lead) => lead.agentName === memberAgentName);
  }, [leadState, memberAgentName]);

  const filteredLeads = useMemo(() => {
    if (weekFilter === "all") return memberLeads;

    return memberLeads.filter((lead) => {
      const leadWeek = getWeekNumber(new Date(lead.createdAt));
      return leadWeek === Number(weekFilter);
    });
  }, [memberLeads, weekFilter]);

  const totalLeads = filteredLeads.length;


    const lostStatuses = [
    "bought_elsewhere",
    "not_interested",
    "no_budget",
    "no_response",
    "spam",
  ];

  const contacted = filteredLeads.filter(
    (lead) => lead.status !== "new"
  ).length;

  const inProgress = filteredLeads.filter(
    (lead) => lead.status === "follow_up"
  ).length;

  const appointmentBooked = filteredLeads.filter(
    (lead) => lead.status === "appointment_booked"
  ).length;

  // Deals closed
const dealsClosed = filteredLeads.filter(
  (lead) => lead.status === "deal_closed"
).length;

// Lost (all lost variations)
const dealsLost = filteredLeads.filter(
  (lead) =>
    lead.status === "lost" ||
    lostStatuses.includes(lead.status)
).length;


  const weekOptions = [
    { value: "all", label: "All Weeks" },
    { value: currentWeek, label: `Current Week (${currentWeek})` },
    { value: currentWeek - 1, label: `Week ${currentWeek - 1}` },
    { value: currentWeek - 2, label: `Week ${currentWeek - 2}` },
    { value: currentWeek - 3, label: `Week ${currentWeek - 3}` },
  ];

  if (isLoading && !member) return <p>Loading team member...</p>;
  if (!member) return <p>Team member not found</p>;

const formatStatus = (status) => {
  if (!status) return "";
  return status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

  return (
    <div>
      <div className="w-100 team-header d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <Avatar size={80} icon={<UserOutlined />} />

          <div>
            <h3 className="mb-1">{displayName}</h3>
            <Tag color="blue">Leads Overview</Tag>
          </div>
        </div>

        <div className="d-flex gap-3 align-items-center">
          <Select
            style={{ width: 200 }}
            value={weekFilter}
            onChange={(value) => setWeekFilter(value)}
            options={weekOptions}
          />

          <div
            className="back__btn d-flex align-items-center"
            style={{ cursor: "pointer" }}
            onClick={() => navigate(-1)}
          >
            <IoIosArrowRoundBack size={26} />
            Back
          </div>
        </div>
      </div>

<div className="leads__count-wrapper g-3 mb-4">

  {/* Total Leads */}
  <div className="col-xl-3 col-lg-3 col-md-6 col-12">
    <div
      className="bg-white p-3 rounded-3 shadow-sm"
      style={{ borderLeft: "5px solid #4f46e5" }}
    >
      <small style={{ color: "#4f46e5" }}>Total Leads</small>
      <h4 className="mb-0" style={{ color: "#1e1b4b" }}>
        {totalLeads}
      </h4>
    </div>
  </div>

  {/* Contacted */}
  <div className="col-xl-3 col-lg-3 col-md-6 col-12">
    <div
      className="bg-white p-3 rounded-3 shadow-sm"
      style={{ borderLeft: "5px solid #0891b2" }}
    >
      <small style={{ color: "#0891b2" }}>Contacted</small>
      <h4 className="mb-0" style={{ color: "#164e63" }}>
        {contacted}
      </h4>
    </div>
  </div>

  {/* Follow Ups */}
  <div className="col-xl-3 col-lg-3 col-md-6 col-12">
    <div
      className="bg-white p-3 rounded-3 shadow-sm"
      style={{ borderLeft: "5px solid #ea580c" }}
    >
      <small style={{ color: "#ea580c" }}>Follow Ups</small>
      <h4 className="mb-0" style={{ color: "#7c2d12" }}>
        {inProgress}
      </h4>
    </div>
  </div>



  {/* Deals Lost */}
  <div className="col-xl-3 col-lg-3 col-md-6 col-12">
    <div
      className="bg-white p-3 rounded-3 shadow-sm"
      style={{ borderLeft: "5px solid #dc2626" }}
    >
      <small style={{ color: "#dc2626" }}>Deals Lost</small>
      <h4 className="mb-0" style={{ color: "#7f1d1d" }}>
        {dealsLost}
      </h4>
    </div>
  </div>



  {/* Appointment Booked */}
  <div className="col-xl-3 col-lg-3 col-md-6 col-12">
    <div
      className="bg-white p-3 rounded-3 shadow-sm"
      style={{ borderLeft: "5px solid #7c3aed" }}
    >
      <small style={{ color: "#7c3aed" }}>Appointment Booked</small>
      <h4 className="mb-0" style={{ color: "#4c1d95" }}>
        {appointmentBooked}
      </h4>
    </div>
  </div>

  {/* Deals Closed */}
  <div className="col-xl-3 col-lg-3 col-md-6 col-12">
    <div
      className="bg-white p-3 rounded-3 shadow-sm"
      style={{ borderLeft: "5px solid #16a34a" }}
    >
      <small style={{ color: "#16a34a" }}>Deals Closed</small>
      <h4 className="mb-0" style={{ color: "#14532d" }}>
        {dealsClosed}
      </h4>
    </div>
  </div>


</div>

<div className="bg-white p-4 rounded-3">
  <Table
    dataSource={filteredLeads.map((lead, index) => ({
      key: lead._id,
      index: index + 1,
      name: lead.name || "Client",
      message: lead.message,
      status: lead.status,
      date: new Date(lead.createdAt).toLocaleDateString(),
    }))}
    columns={[
      {
        title: "SNo",
        dataIndex: "index",
      },
      {
        title: "Name",
        dataIndex: "name",
      },
      {
        title: "Message",
        dataIndex: "message",
        ellipsis: true,
      },
      {
        title: "Status",
        render: (_, record) => {
          let color = "blue";

          if (record.status === "new") color = "green";
          if (record.status === "contacted") color = "orange";
          if (record.status === "follow_up") color = "purple";
          if (record.status === "appointment_booked") color = "cyan";
          if (record.status === "deal_closed") color = "blue";
          if (
            [
              "bought_elsewhere",
              "not_interested",
              "no_budget",
              "no_response",
              "spam"
            ].includes(record.status)
          ) {
            color = "red";
          }

          return <Tag color={color}>{formatStatus(record.status)}</Tag>;
        },
      },
      {
        title: "Date",
        dataIndex: "date",
      },
    ]}
  />
</div>
    </div>
  );
};

export default TeamSinglePage;